import React, { useCallback, useState } from 'react';
import { Stack, Box, Button, Link, Typography } from '@mui/material';
import User, { Company } from '../../../interfaces/users.interface';
import { DataCell, DataRow } from '../../Tables/DataList';
import { tightTableSx } from '../../Tables/dataTableStyles';
import StandardDialog, { StandardDialogActions } from '../../Modals/StandardDialog';
import { getCompanyPendingUsers } from '../../../services/companyDetail';
import { handlePendingUser } from '../../../services/user';
import { useCompanyResource } from '../../Vendors/useCompanyResource';
import { TabEmpty, TabSkeletonRows, DEFAULT_PER_PAGE } from '../../Vendors/companyTabUi';
import { useSectionPager } from '../../Vendors/sectionPager';

type PendingUsersTabProps = {
  company: Company;
  companyCallback: any;
  /** Called after an approve/reject so the parent can refresh the pending count. */
  onMembershipChange?: () => void;
};

export default function PendingUsersTab({ company, companyCallback, onMembershipChange }: PendingUsersTabProps) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PER_PAGE);
  const { items: pendingUsers, count, loading, refetch } = useCompanyResource<User>(
    getCompanyPendingUsers,
    company.id,
    page,
    pageSize,
  );
  useSectionPager({ count, page, pageSize, setPage, setPageSize });

  const [curUser, setCurUser] = useState<User | undefined>(undefined);
  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const closeDialogs = () => { setApproveOpen(false); setRejectOpen(false); setCurUser(undefined); };

  const handlePendingAction = useCallback(async (action: 'accept' | 'reject') => {
    if (!curUser) return;
    const updated = await handlePendingUser(curUser.id, company, action);
    companyCallback(updated);
    closeDialogs();
    refetch();
    onMembershipChange?.();
  }, [curUser, company, companyCallback, refetch, onMembershipChange]);

  const fullName = curUser ? `${curUser.firstName} ${curUser.lastName}` : '';

  return (
    <Stack spacing={2} sx={{ p: 2 }}>
      <Box sx={tightTableSx}>
        <DataRow header>
          <DataCell xs={4}>Email</DataCell>
          <DataCell xs={3}>First</DataCell>
          <DataCell xs={3}>Last</DataCell>
          <DataCell xs={2}>Approve/Reject</DataCell>
        </DataRow>

        {loading && <TabSkeletonRows rows={3} columns={[4, 3, 3, 2]} />}

        {!loading && pendingUsers.length === 0 && <TabEmpty label='No pending users' />}

        {!loading && pendingUsers.map((user) => (
          <DataRow key={user.id} onClick={() => {}} hover>
            <DataCell xs={4}><Link href={`mailto:${user.email}`}>{user.email}</Link></DataCell>
            <DataCell xs={3}>{user.firstName}</DataCell>
            <DataCell xs={3}>{user.lastName}</DataCell>
            <DataCell xs={2}>
              <Stack spacing={1} alignItems='flex-start'>
                <Button variant='outlined' sx={{ width: '150px' }} onClick={(e) => { e.stopPropagation(); setCurUser(user); setApproveOpen(true); }}>Approve</Button>
                <Button variant='outlined' color='error' sx={{ width: '150px' }} onClick={(e) => { e.stopPropagation(); setCurUser(user); setRejectOpen(true); }}>Reject</Button>
              </Stack>
            </DataCell>
          </DataRow>
        ))}
      </Box>


      <StandardDialog title={`Reject ${fullName}`} handleClose={closeDialogs} isOpen={rejectOpen}>
        <Typography>Are you sure you want to reject {fullName}?</Typography>
        <StandardDialogActions>
          <Button variant='outlined' onClick={closeDialogs}>Cancel</Button>
          <Button variant='contained' color='error' onClick={() => handlePendingAction('reject')}>Reject</Button>
        </StandardDialogActions>
      </StandardDialog>
      <StandardDialog title={`Approve ${fullName}`} handleClose={closeDialogs} isOpen={approveOpen}>
        <Typography>Are you sure you want to approve {fullName}?</Typography>
        <StandardDialogActions>
          <Button variant='outlined' onClick={closeDialogs}>Cancel</Button>
          <Button variant='contained' onClick={() => handlePendingAction('accept')}>Approve</Button>
        </StandardDialogActions>
      </StandardDialog>
    </Stack>
  );
}
